import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/** The address parameter that asks a page to start a new record. */
export const NEW_PARAM = 'new';

/** The words to start the record with, such as the name that was searched for. */
export const NEW_NAME_PARAM = 'name';

/**
 * Where to go to start a new record on a page that creates it in a dialog.
 *
 * @param path the page, such as `/customers`
 * @param name words to start the record with, when there are any
 * @returns the path with the request in its query string
 */
export function newRequestPath(path: string, name?: string): string {
  const params = new URLSearchParams({ [NEW_PARAM]: '1' });
  if (name !== undefined && name.trim() !== '') {
    params.set(NEW_NAME_PARAM, name.trim());
  }
  return `${path}?${params.toString()}`;
}

export interface NewRequest {
  /** True while the address asks this page to start a new record. */
  readonly requested: boolean;
  /** The words the request carries, such as a customer's name, or null. */
  readonly name: string | null;
  /** Says the request has been taken up, by taking it out of the address without a history entry. */
  readonly dismiss: () => void;
}

/**
 * Reads the shell's request to start a new record.
 *
 * The "New" menu and the search box send people to a page with `?new=1` so that the page's own
 * dialog opens on arrival. A page that creates its record in a dialog treats `requested` as one
 * more reason for the dialog to be open, once whatever it needs to be usable has loaded, and calls
 * `dismiss` when the dialog closes or saves, so that a reload or a back step does not reopen it.
 *
 * @returns the request, and the way to say it has been handled
 */
export function useNewRequest(): NewRequest {
  const [params, setParams] = useSearchParams();
  const requested = params.get(NEW_PARAM) === '1';
  const typed = requested ? params.get(NEW_NAME_PARAM)?.trim() : undefined;

  const dismiss = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(NEW_PARAM);
        next.delete(NEW_NAME_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  return { requested, name: typed === undefined || typed === '' ? null : typed, dismiss };
}
